'use es6';

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import I18n from 'I18n';
import styled from 'styled-components';
import H5 from 'UIComponents/elements/headings/H5';
import UIFieldset from 'UIComponents/form/UIFieldset';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelect from 'UIComponents/input/UISelect';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIToggle from 'UIComponents/input/UIToggle';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextArea from 'UIComponents/input/UITextArea';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import StyleProtectedUIDropdown from '../style-protected/StyleProtectedUIDropdown';
import { toolbarI18nKey, textButtonStyles } from '../../constants/toolbar';
import { TableBorderStyles, TableDimensionTypes } from '../../constants/table';
import { isPercentage, pixelStringToNumber } from '../../utils/table';
import PixelStepperInput from '../table/controls/TablePopoverPixelStepperInput';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `RichTextLib.table.toolbar.${suffix}`;
const extractEventValue = (fn, key = 'value') => evt => {
  const {
    [key]: value
  } = evt.target;
  fn(value);
};
const StyleControlsContainer = styled(UIFieldset).withConfig({
  displayName: "TableStyleControlButton__StyleControlsContainer",
  componentId: "sc-7sk18k-0"
})(["min-width:280px;max-width:340px;"]);
const StyleFormLabel = styled(UIFormLabel).withConfig({
  displayName: "TableStyleControlButton__StyleFormLabel",
  componentId: "sc-7sk18k-1"
})(["padding-top:4px;padding-bottom:4px;font-size:14px !important;font-weight:400 !important;"]);
const StyleFormLabelWithInfo = styled(UIHelpIcon).withConfig({
  displayName: "TableStyleControlButton__StyleFormLabelWithInfo",
  componentId: "sc-7sk18k-2"
})(["font-size:14px !important;font-weight:400 !important;& span{font-size:14px !important;}"]);
const StyleSelect = styled(UISelect).withConfig({
  displayName: "TableStyleControlButton__StyleSelect",
  componentId: "sc-7sk18k-3"
})(["max-width:144px !important;"]);
const StyleToggle = styled(UIToggle).withConfig({
  displayName: "TableStyleControlButton__StyleToggle",
  componentId: "sc-7sk18k-4"
})(["max-width:144px !important;"]);
const StyleFieldLabel = styled(FormattedMessage).withConfig({
  displayName: "TableStyleControlButton__StyleFieldLabel",
  componentId: "sc-7sk18k-5"
})(["font-size:14px !important;font-weight:400 !important;"]);
const StyleFieldTextArea = styled(UITextArea).withConfig({
  displayName: "TableStyleControlButton__StyleFieldTextArea",
  componentId: "sc-7sk18k-6"
})(["height:76px !important;"]);
const propTypes = {
  className: PropTypes.string.isRequired,
  dropdownClassName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  tableWidth: PropTypes.string.isRequired,
  borderWidth: PropTypes.number.isRequired,
  borderStyle: PropTypes.oneOf(TableBorderStyles).isRequired,
  cellPadding: PropTypes.number.isRequired,
  tableCaption: PropTypes.bool.isRequired,
  tableIsResponsive: PropTypes.bool.isRequired,
  tableStyle: PropTypes.string.isRequired,
  onBorderWidthChange: PropTypes.func.isRequired,
  onBorderStyleChange: PropTypes.func.isRequired,
  onTableDimensionTypeChange: PropTypes.func.isRequired,
  onCellPaddingChange: PropTypes.func.isRequired,
  onTableCaptionChange: PropTypes.func.isRequired,
  onTableResponsiveChange: PropTypes.func.isRequired,
  onTableCustomStyleChange: PropTypes.func.isRequired
};
function TableStyleControlButton(props) {
  const {
    className,
    dropdownClassName,
    disabled,
    tableWidth,
    borderWidth,
    onBorderWidthChange,
    borderStyle,
    onBorderStyleChange,
    onTableDimensionTypeChange,
    cellPadding,
    onCellPaddingChange,
    tableCaption,
    onTableCaptionChange,
    tableIsResponsive,
    onTableResponsiveChange,
    tableStyle: _tableStyle,
    onTableCustomStyleChange
  } = props;
  const [handleBorderWidthChange, handleBorderWidthStep] = useDebouncedOnChange(extractEventValue(onBorderWidthChange));
  const [handleCellPaddingChange, handleCellPaddingStep] = useDebouncedOnChange(extractEventValue(onCellPaddingChange));
  const [tableStyle, setTableStyle] = useState(_tableStyle);
  const [onTableCssChange] = useDebouncedOnChange(onTableCustomStyleChange);
  const handleTableCustomCssChange = evt => {
    const {
      value
    } = evt.target;
    setTableStyle(value);
    onTableCssChange(value);
  };
  useEffect(() => {
    setTableStyle(_tableStyle);
  }, [_tableStyle]);
  return /*#__PURE__*/_jsx(StyleProtectedUIDropdown, {
    "data-test-id": "table-style-control-button",
    use: "unstyled",
    buttonText: I18n.text(toolbarI18nKey('tableStyle')),
    buttonUse: "transparent",
    buttonClassName: className,
    autoPlacement: "horiz",
    placement: "bottom right",
    closeOnMenuClick: false,
    dropdownClassName: dropdownClassName,
    animateOnToggle: false,
    disabled: disabled,
    arrowSize: "medium",
    children: /*#__PURE__*/_jsxs(StyleControlsContainer, {
      "data-test-id": "table-style-control-content",
      className: "m-all-4",
      _size: "small",
      children: [/*#__PURE__*/_jsx(H5, {
        className: "m-bottom-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('table.title')
        })
      }), /*#__PURE__*/_jsxs(UIFlex, {
        alignment: "center",
        justify: "between",
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(StyleFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('borderWidth')
          })
        }), /*#__PURE__*/_jsx(PixelStepperInput, {
          "data-test-id": "table-border-width-input",
          min: 0,
          max: 30,
          width: "144px",
          value: pixelStringToNumber(borderWidth),
          onChange: handleBorderWidthChange,
          onStep: handleBorderWidthStep
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        alignment: "center",
        justify: "between",
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(StyleFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('borderStyle')
          })
        }), /*#__PURE__*/_jsx(StyleSelect, {
          "data-test-id": "table-border-style-select",
          dropdownClassName: dropdownClassName,
          options: TableBorderStyles.map(style => ({
            value: style,
            text: I18n.text(i18nKey(`borderStyleOptions.${style}`))
          })),
          value: borderStyle,
          onChange: extractEventValue(onBorderStyleChange)
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        alignment: "center",
        justify: "between",
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(StyleFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('table.widthType')
          })
        }), /*#__PURE__*/_jsx(StyleSelect, {
          "data-test-id": "table-dimension-type-select",
          dropdownClassName: dropdownClassName,
          options: Object.values(TableDimensionTypes).map(type => ({
            value: type,
            text: I18n.text(i18nKey(`table.${type}`))
          })),
          value: isPercentage(tableWidth) ? TableDimensionTypes.PERCENTAGE : TableDimensionTypes.PIXELS,
          onChange: extractEventValue(onTableDimensionTypeChange)
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        alignment: "center",
        justify: "between",
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(StyleFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('table.cellPadding')
          })
        }), /*#__PURE__*/_jsx(PixelStepperInput, {
          "data-test-id": "table-cell-padding-input",
          min: 0,
          max: 40,
          width: "144px",
          value: pixelStringToNumber(cellPadding),
          onChange: handleCellPaddingChange,
          onStep: handleCellPaddingStep
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        alignment: "center",
        justify: "between",
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(StyleFormLabelWithInfo, {
          title: I18n.text(i18nKey('table.captionTooltip')),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('table.caption')
          })
        }), /*#__PURE__*/_jsx(StyleToggle, {
          "data-test-id": "table-caption-toggle",
          size: "xs",
          checked: tableCaption,
          onChange: extractEventValue(onTableCaptionChange, 'checked')
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        alignment: "center",
        justify: "between",
        className: "m-top-4",
        children: [/*#__PURE__*/_jsx(StyleFormLabelWithInfo, {
          title: I18n.text(i18nKey('table.responsiveTooltip')),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('table.responsive')
          })
        }), /*#__PURE__*/_jsx(StyleToggle, {
          "data-test-id": "table-responsive-toggle",
          size: "xs",
          checked: tableIsResponsive,
          onChange: extractEventValue(onTableResponsiveChange, 'checked')
        })]
      }), /*#__PURE__*/_jsx(UIFormControl, {
        className: "m-top-2",
        label: /*#__PURE__*/_jsx(StyleFieldLabel, {
          message: i18nKey('table.styleField')
        }),
        tooltip: I18n.text(i18nKey('table.styleFieldTooltip')),
        children: /*#__PURE__*/_jsx(StyleFieldTextArea, {
          "data-test-id": "table-custom-css-input",
          rows: 3,
          resize: false,
          value: tableStyle,
          onChange: handleTableCustomCssChange
        })
      })]
    })
  });
}
TableStyleControlButton.propTypes = propTypes;
TableStyleControlButton.displayName = 'TableStyleControlButton';
const StyledTableStyleControlButton = styled(TableStyleControlButton).withConfig({
  displayName: "TableStyleControlButton__StyledTableStyleControlButton",
  componentId: "sc-7sk18k-7"
})(["", ""], textButtonStyles);
export default StyledTableStyleControlButton;